<template>
  <dashboard-card class="db-monthly" title="Faturamento mensal" :cut="cut" :note="note">
    <div class="loading loading-lg db-card-loading" v-if="loading"></div>

    <dashboard-empty
      v-else-if="isEmpty"
      icon="chart-line"
      title="Sem histórico para comparar"
      reason="Ainda não há lotes enviados nos últimos meses. O gráfico compara faturado, recebido
              e glosas assim que o primeiro lote for fechado."
      action-to="/financial/billing-batches"
      action-label="Ir para Lotes"
      action-icon="layer-group"
    />

    <div class="db-chart" v-else>
      <div class="db-legend">
        <span class="db-legend-billed"><i></i>Faturado</span>
        <span class="db-legend-received"><i></i>Recebido</span>
        <span class="db-legend-glossed"><i></i>Glosas</span>
      </div>
      <st-bar-chart :chart-data="chartData" :options="options" :height="220"/>
    </div>
  </dashboard-card>
</template>

<script>
import currency from '@/filters/currency';
import number from '@/filters/number';
import {
  chartColors, competenceMonth, competenceName, pluralize,
} from '../helpers';
import DashboardCard from './DashboardCard.vue';
import DashboardEmpty from './DashboardEmpty.vue';

const VALUE_AXIS = 'db-y-value';

export default {
  components: {
    DashboardCard,
    DashboardEmpty,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    competence: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isEmpty() {
      return this.items.length === 0
        || this.items.every(item => item.informed === 0
          && item.received === 0 && item.glossed === 0);
    },
    cut() {
      if (this.items.length === 0) return 'Últimos 6 meses';
      return `Últimos ${pluralize(this.items.length, 'mês', 'meses')}`;
    },
    note() {
      if (this.loading || this.items.length === 0) return '';
      const last = this.items[this.items.length - 1];
      const end = competenceName(last.competence || this.competence);
      return `${pluralize(this.items.length, 'mês', 'meses')} até ${end}`;
    },
    chartData() {
      return {
        labels: this.items.map(item => competenceMonth(item.competence)),
        datasets: [
          {
            label: 'Faturado',
            yAxisID: VALUE_AXIS,
            data: this.items.map(item => item.informed),
            backgroundColor: chartColors.billedFill,
            borderColor: chartColors.billed,
            borderWidth: 1,
          },
          {
            label: 'Recebido',
            yAxisID: VALUE_AXIS,
            data: this.items.map(item => item.received),
            backgroundColor: chartColors.receivedFill,
            borderColor: chartColors.received,
            borderWidth: 1,
          },
          {
            label: 'Glosas',
            type: 'line',
            yAxisID: VALUE_AXIS,
            data: this.items.map(item => item.glossed),
            backgroundColor: chartColors.glossedFill,
            borderColor: chartColors.glossed,
            borderDash: [4, 3],
            borderWidth: 2,
            pointBackgroundColor: chartColors.glossed,
            pointRadius: 3,
            lineTension: 0,
            fill: false,
          },
        ],
      };
    },
    options() {
      return {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label(item, data) {
              const label = data.datasets[item.datasetIndex].label || '';
              return `${label}: ${currency(item.yLabel)}`;
            },
          },
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false,
            },
          }],
          yAxes: [
            {
              id: VALUE_AXIS,
              position: 'left',
              ticks: {
                beginAtZero: true,
                callback: value => number(value, 0),
              },
            },
          ],
        },
      };
    },
  },
};
</script>
